import * as babylon from "@babylonjs/core";

import { ThreeDPanelState } from "@/shared/state/visualization/schema/v1";

import { DataServerManager } from "../DataServer/DataServerManager.ts";
import {
  FALLBACK_EXTENT_M,
  MODEL_FRAMING_ALPHA_RAD,
  MODEL_FRAMING_BETA_RAD,
  MODEL_FRAMING_RADIUS_PER_EXTENT,
  modelBoundsOf,
} from "../modelFraming.ts";
import { type ModelAssetLoader } from "../ModelLoader/index.ts";

import { initScene } from "./assetBuilders/scene.ts";
import {
  clearMeasurements,
  removeMeasurement,
} from "./measure/measureSession.ts";
import {
  BabylonState,
  BabylonTheme,
  Callbacks,
  ToolMode,
  Vector3Plain,
  ZERO_CAMERA_DELTAS,
} from "./state.ts";
import * as systems from "./systems/index.ts";

/**
 * Snapshot of the active camera's framing fields, as plain numbers and
 * a `Vector3Plain` target. Read by the mode toggle to seed a
 * third-person spec from a live orbit camera (alpha/beta/radius and the
 * followed entity's data-space position) without leaking Babylon
 * internals across the React boundary.
 */
export interface CameraSnapshot {
  alpha: number;
  beta: number;
  radius: number;
  target: Vector3Plain;
}

/**
 * Pure helper. Returns a data-space snapshot of the active camera's
 * framing, or `undefined` when no camera is built.
 *
 * `target` is the camera's data-space focus, read from
 * `camera.focusPoint` (the authoritative render origin written by
 * `updateCamera` each frame). Not the same as `babylonCamera.target`,
 * which stays at `Vector3.Zero()` for both modes — capturing that
 * would seed `focusPoint = (0,0,0)` on the toggle and place the
 * third-person camera nowhere near the actual content. The plain
 * `Vector3Plain` return keeps the React boundary free of Babylon types.
 */
export function cameraSnapshot(
  state: BabylonState,
): CameraSnapshot | undefined {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return undefined;
  }
  const target = findDataSpaceTarget(state);
  return {
    alpha: camera.babylonCamera.alpha,
    beta: camera.babylonCamera.beta,
    radius: camera.babylonCamera.radius,
    target: { x: target.x, y: target.y, z: target.z },
  };
}

function findDataSpaceTarget(state: BabylonState): {
  x: number;
  y: number;
  z: number;
} {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return { x: 0, y: 0, z: 0 };
  }
  // Both modes' `camera.focusPoint` is the data-space focus, written by
  // `updateCamera` each frame. For third-person it's the value the user
  // is panning/elevating to; for orbit it mirrors the tracked entity.
  // Returning a `Vector3Plain` keeps the React boundary free of Babylon
  // types.
  const fp = camera.focusPoint;
  return { x: fp.x, y: fp.y, z: fp.z };
}

/**
 * Frames `target` the way an imported model is framed: from the front, at a
 * distance proportional to its size, looking at the middle of what it draws.
 *
 * Returns `undefined` when no camera is built or the entity's position is
 * unavailable, which is what disables the button.
 *
 * ## Why it measures, and why measuring is allowed here
 *
 * This used to return `ORBIT_DEFAULT_ALPHA_RAD` (45°, the robot's *back*), the
 * orbit envelope's initial radius (20.8m, whatever the target's size), and the
 * entity's position (a URDF's base link, on the floor) — so one click on the
 * recenter control left the user looking at the back of a robot's feet from
 * across the room. Sharing `modelFraming`'s constants is what stops this and the
 * ingest preview from disagreeing about what "framed" means.
 *
 * Third-person only, because the button is (`RecenterButton`). An orbit camera
 * re-frames itself: it tracks its target every frame, and the target may name
 * one node of the model rather than the whole entity.
 *
 * The bounds are read off the **live** scene, which everywhere else in this
 * package is a defect: `constrainCamera` caches its envelope on
 * `entityGeneration` precisely because measuring a posed hierarchy per frame
 * re-meshed the grid 40 times per playback sweep. This is on a *click*, and it
 * writes only the Babylon camera and `camera.focusPoint` — no generation bump,
 * no cache invalidation, nothing the render loop reruns.
 *
 * Live mesh bounds are in **render** space: `applyRenderOrigin` shifts every
 * mesh by `-camera.focusPoint` each frame. So the measured centre is put back
 * into data space by adding the focus point, which is where `CameraSnapshot`'s
 * `target` lives and what {@link BabylonManager.recenter} writes back into it.
 */
export function recenterFraming(
  state: BabylonState,
  target: { id: string; mesh: "mesh" | "path" },
): CameraSnapshot | undefined {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return undefined;
  }
  // Absolute, not the entity's local offset — recentering on a child link has to
  // frame where that link actually is in the world.
  const source = systems.targetPosition(state, target);
  if (!source) {
    return undefined;
  }
  const entity = state.entities[target.id];
  const drawn =
    target.mesh === "mesh"
      ? systems.meshBounds(entity?.mesh)
      : systems.boundsOf(entity?.path?.mesh);
  // Nothing drawn is a model whose file is still loading. The position is then
  // the only answer there is, at the scale everything this panel draws is.
  const bounds = drawn
    ? modelBoundsOf(
        drawn.min.add(camera.focusPoint),
        drawn.max.add(camera.focusPoint),
      )
    : {
        extent: FALLBACK_EXTENT_M,
        center: { x: source.x, y: source.y, z: source.z },
      };
  return {
    alpha: MODEL_FRAMING_ALPHA_RAD,
    beta: MODEL_FRAMING_BETA_RAD,
    radius: bounds.extent * MODEL_FRAMING_RADIUS_PER_EXTENT,
    target: bounds.center,
  };
}

export type Params = {
  dataServer: DataServerManager;
  modelLoader: ModelAssetLoader;
  canvas: HTMLCanvasElement;
  time: bigint;
  theme: BabylonTheme;
  callbacks: Callbacks;
  initialState: ThreeDPanelState;
};

/**
 * State manager for a BabylonJS visualization driven by the VizState and visualization timer.
 *
 * Draws inspiration from the ECS pattern, where systems mutate the long-lived mutable state
 * of entities instead of reducers producing immutable, ephemeral state copies.
 *
 * For more details, see the subpackage README.md.
 */
export class BabylonManager {
  private readonly state: BabylonState;
  disposed = false;

  constructor(params: Params) {
    this.state = initState(params);
  }

  setState(panelState: ThreeDPanelState) {
    if (this.disposed) {
      return;
    }
    systems.onSetState(this.state, panelState);
  }

  setTime(time: bigint) {
    if (this.disposed) {
      return;
    }
    systems.onSetTime(this.state, time);
  }

  setTheme(theme: BabylonTheme) {
    if (this.disposed) {
      return;
    }
    systems.onSetTheme(this.state, theme);
  }

  /**
   * Read-only snapshot of the active camera's framing fields. Returns
   * `undefined` when the manager is disposed or no camera is built yet
   * (e.g. before the first `setState`). The mode toggle calls this to
   * capture a live orbit camera's framing into a third-person spec.
   */
  getCameraSnapshot(): CameraSnapshot | undefined {
    if (this.disposed) {
      return undefined;
    }
    return cameraSnapshot(this.state);
  }

  /**
   * Apply {@link recenterFraming}'s framing for `target` to the live camera.
   * Imperative — does not round-trip through panel state, so
   * (a) consecutive clicks always re-apply even if the framing values
   * haven't changed, and (b) live framing isn't persisted (covered by
   * the broader live-framing-persistence TODO).
   */
  recenter(target: { id: string; mesh: "mesh" | "path" }): void {
    if (this.disposed) {
      return;
    }
    const framing = recenterFraming(this.state, target);
    if (!framing) {
      return;
    }
    const camera = this.state.sceneConfig.camera;
    if (!camera) {
      return;
    }
    camera.babylonCamera.alpha = framing.alpha;
    camera.babylonCamera.beta = framing.beta;
    camera.babylonCamera.radius = framing.radius;
    camera.focusPoint.set(framing.target.x, framing.target.y, framing.target.z);
    this.state.input.cameraDeltas = { ...ZERO_CAMERA_DELTAS };
  }

  /**
   * Moves one joint to a value, drawn from the next frame on.
   *
   * Imperative, and deliberately **not** routed through `setState`. A joint's
   * live value is component state; the panel state is a declaration — a rest
   * pose, or a topic binding. Pushing a value through it reaches
   * `updateEntities` as a changed `Rotation` spec, and the only thing that can
   * do with one is dispose the component and recreate it at identity, stale for
   * the next `reloadStaleComponents` poll. At a 16ms frame and a 50ms poll, that
   * is roughly three frames of this joint and its whole descendant subtree
   * snapped back to the rest pose — on *every* tick of whatever is driving it.
   *
   * `pose.motion` says whether the joint turns its link (`revolute`,
   * `continuous`) or slides it along the axis (`prismatic`); `pose.axis` is the
   * axis the model declared, used verbatim and normalized on read. The spec is
   * left exactly as it was, and `clearJointPose` is how a joint returns to it.
   *
   * @param entityId the entity the joint moves — a joint's **child link**, which
   *   is what `schema.Joint.drives` names
   */
  setJointPose(entityId: string, pose: systems.JointPoseInput) {
    if (this.disposed) {
      return;
    }
    systems.onSetJointPose(this.state, entityId, pose);
  }

  /** Returns a joint to the rest pose its spec declares. */
  clearJointPose(entityId: string) {
    if (this.disposed) {
      return;
    }
    systems.onClearJointPose(this.state, entityId);
  }

  /**
   * The value a joint is being driven to, or `undefined` if nothing is driving
   * it and it therefore sits where its spec puts it.
   */
  getJointPose(entityId: string): number | undefined {
    if (this.disposed) {
      return undefined;
    }
    return systems.jointPoseValue(this.state, entityId);
  }

  /**
   * Moves one joint of an entity's model to a value, drawn from the next frame
   * on.
   *
   * The model counterpart of {@link setJointPose}, and imperative for the same
   * reason it is: a joint's live value is component state, while the panel state
   * is a declaration — a topic binding or a constant. Pushing a value through
   * `setState` reaches `updateEntities` as a changed `jointValues` spec, whose
   * only outcome is a restaled joint cache waiting on the next
   * `reloadStaleComponents` poll — roughly three frames of this joint and
   * everything below it back at the rest pose, on every tick of whatever is
   * driving it.
   *
   * The joint is named as the model file names it, and the file's own
   * declaration says whether that value turns the link or slides it and about
   * which axis — so, unlike {@link setJointPose}, there is no axis to hand over
   * and no way for a caller to disagree with the model about how a joint moves.
   * Radians for a joint that turns, metres for one that slides.
   *
   * Only the named joint is affected; every other joint keeps playing back its
   * binding. A joint coupled by `mimic` is computed from its driver and cannot
   * be driven directly — drive the driver. `clearModelJointPose` is how a joint
   * goes back to its binding.
   */
  setModelJointPose(entityId: string, joint: string, value: number) {
    if (this.disposed) {
      return;
    }
    systems.onSetModelJointPose(this.state, entityId, joint, value);
  }

  /** Returns one of a model's joints to whatever its spec says it is at. */
  clearModelJointPose(entityId: string, joint: string) {
    if (this.disposed) {
      return;
    }
    systems.onClearModelJointPose(this.state, entityId, joint);
  }

  /**
   * The value one of a model's joints is being driven to, or `undefined` if
   * nothing is driving it and it therefore follows its binding.
   */
  getModelJointPose(entityId: string, joint: string): number | undefined {
    if (this.disposed) {
      return undefined;
    }
    return systems.modelJointPoseValue(this.state, entityId, joint);
  }

  /**
   * Turns the measure tool on or off, in effect from the next frame.
   *
   * Imperative, like the joint-pose family: tool mode is transient
   * interaction state, never persisted, so it does not route through
   * `setState`. Hover reporting flows through `reportToolHover` on the
   * render loop, which emits a clearing `null` after a switch to `"none"`.
   */
  setToolMode(mode: ToolMode): void {
    if (this.disposed) {
      return;
    }
    const tool = this.state.input.tool;
    if (!tool || tool.mode === mode) {
      return;
    }
    tool.mode = mode;
    // Both writes are needed. Babylon's scene input manager reassigns
    // `canvas.style.cursor = scene.defaultCursor` on every pointer move,
    // so the element write alone lasts only until the pointer moves. And
    // `defaultCursor` alone takes effect only on that next move, leaving
    // the cursor stale when the tool is switched with the pointer already
    // over the canvas.
    const cursor = mode === "none" ? "" : "crosshair";
    this.state.context.scene.defaultCursor = cursor;
    this.state.context.canvas.style.cursor = cursor;
  }

  /**
   * Drops every completed measurement and any in-progress anchor. The
   * scene's lines and dots follow on the next frame: `renderMeasures`
   * reconciles visuals against the measure state, so no disposal is
   * needed here.
   */
  clearMeasurements(): void {
    if (this.disposed) {
      return;
    }
    const tool = this.state.input.tool;
    if (!tool?.measure) {
      return;
    }
    tool.measure = clearMeasurements(tool.measure);
  }

  /**
   * Drops one completed measurement, leaving the rest and any in-progress
   * anchor. The scene follows on the next frame, for the same reason
   * {@link clearMeasurements} needs no disposal here.
   */
  removeMeasurement(id: number): void {
    if (this.disposed) {
      return;
    }
    const tool = this.state.input.tool;
    if (!tool?.measure) {
      return;
    }
    tool.measure = removeMeasurement(tool.measure, id);
  }

  /**
   * Stops all scheduled systems, prevents new systems from being scheduled,
   * and disposes all of the state's resources.
   *
   * Everything a caller can observe happens synchronously: no system runs, no
   * frame renders and no entity survives past the call. Only the GPU handles
   * may be released a moment later, and only while Babylon itself is still
   * using them — see {@link whenBabylonIsDoneWithTheGpu}.
   */
  dispose() {
    this.disposed = true;
    const ctx = this.state.context;
    for (const schedule of Object.values(ctx.systemSchedules)) {
      schedule.dispose();
    }
    // Undo `setToolMode`'s crosshair, in case the canvas outlives this
    // manager.
    ctx.scene.defaultCursor = "";
    ctx.canvas.style.cursor = "";
    // Dispose all entities in the scene.
    systems.onSetState(this.state, { data: { entities: {} } });

    // Dispose the scene and engine.
    const releaseGpu = () => {
      ctx.scene.dispose();
      ctx.engine.dispose();
      ctx.dataServer.dispose();
    };
    const pending = whenBabylonIsDoneWithTheGpu(ctx);
    if (pending === null) {
      releaseGpu();
    } else {
      void pending.then(releaseGpu);
    }
  }
}

/** How long {@link whenBrdfSettles} waits for the texture expansion before
 *  taking the GPU back anyway. Generous: the wait costs nothing visible (the
 *  manager is already inert), and expiring it is what the wait exists to
 *  avoid. */
const BRDF_EXPANSION_TIMEOUT_MS = 5_000;
const BRDF_EXPANSION_POLL_MS = 16;

/**
 * Resolves once nothing Babylon started in the background still needs this
 * scene's engine — or `null` when nothing does, so the caller can tear down
 * synchronously in the ordinary case.
 *
 * There is one such thing: the environment BRDF texture. Any PBR material
 * pulls it in (`BRDFTextureTools.GetEnvironmentBRDFTexture`), and it arrives
 * as a base64 PNG that Babylon decodes and then EXPANDS on the GPU — when the
 * image lands, `RGBDTextureTools.ExpandRGBDTexture` builds a post-process,
 * `await`s a dynamically imported shader, and renders the decoded texture
 * through it. That continuation cannot be cancelled and holds no reference to
 * us: when it resumes it simply reaches for `texture.getScene()` and the
 * engine's compiled effect. Dispose in the meantime and it throws into a
 * floating promise — `Cannot read properties of null` — which surfaces as an
 * unhandled rejection rather than anything a caller could catch.
 *
 * `isReady()` is the signal to wait on, because the expansion is what finally
 * sets it: false while the PNG is still decoding AND for as long as the
 * expansion is in flight, true only once it has swapped its result in.
 * `onLoadObservable` is NOT the signal — that fires when the expansion
 * STARTS, which is why Babylon's own `BaseTexture.WhenAllReady` (built on it)
 * would hand the engine back at precisely the wrong moment.
 */
/** The part of a manager's context teardown reads. Structural, and narrow
 *  enough that a test can drive the handover with a stub loader and a stub
 *  texture instead of a WebGL context. Method syntax on `whenSettled` is what
 *  lets a real {@link ModelAssetLoader} — whose parameter is a full
 *  `babylon.Scene` — satisfy it. */
export interface GpuHandoverContext {
  scene: BrdfScene;
  modelLoader: { whenSettled?(scene: BrdfScene): Promise<void> };
}

export function whenBabylonIsDoneWithTheGpu(
  ctx: GpuHandoverContext,
): Promise<void> | null {
  const settled = ctx.modelLoader.whenSettled?.(ctx.scene);
  if (settled === undefined && !isBrdfPending(ctx.scene)) {
    return null;
  }
  // The loader first, THEN the texture its parse may have started. Sequential
  // rather than raced: until the parse is done there may be no texture to wait
  // on yet, so asking both at once would find nothing pending and hand the GPU
  // straight back. The loader's wait is deliberately unbounded — a timeout
  // there would only trade a late release for the crash this exists to
  // prevent, and a stalled fetch costs a delayed GPU release rather than
  // anything the user sees (`dispose` has already returned).
  return Promise.resolve(settled).then(() => whenBrdfSettles(ctx.scene));
}

/** Whether the environment BRDF texture exists and has not finished expanding.
 *  See {@link whenBabylonIsDoneWithTheGpu} for why `isReady` is the signal. */
export function isBrdfPending(scene: BrdfScene): boolean {
  const brdf = scene.environmentBRDFTexture;
  // Absent on a scene no PBR material ever reached, which is nothing to wait
  // for rather than something unfinished.
  if (!brdf) {
    return false;
  }
  return !brdf.isReady();
}

/** The part of a scene {@link whenBrdfSettles} reads — structural so a test can
 *  stand in a texture whose readiness it drives. */
export interface BrdfScene {
  environmentBRDFTexture?: { isReady(): boolean } | null;
}

/**
 * Resolves once the environment BRDF texture has finished expanding, or after
 * {@link BRDF_EXPANSION_TIMEOUT_MS}.
 *
 * Polls rather than observes because the observable Babylon offers fires at
 * the START of the expansion; see {@link whenBabylonIsDoneWithTheGpu}.
 *
 * Times its own budget from the moment it is called, and takes no deadline
 * from its caller. That is load-bearing: teardown waits for the model loader
 * FIRST, and a deadline started before that wait could already have expired by
 * the time the texture exists — so the first poll would resolve immediately
 * and release the engine mid-expansion, which is precisely the crash this all
 * exists to prevent. The budget bounds the expansion, so it starts with it.
 */
export function whenBrdfSettles(scene: BrdfScene): Promise<void> {
  if (!isBrdfPending(scene)) {
    return Promise.resolve();
  }
  const expiry = Date.now() + BRDF_EXPANSION_TIMEOUT_MS;
  return new Promise((resolve) => {
    // The bare globals rather than `window.` ones: this is the only part of
    // the manager that has to run outside a browser, because it is the only
    // part worth testing without one.
    const poll = setInterval(() => {
      if (!isBrdfPending(scene) || Date.now() >= expiry) {
        clearInterval(poll);
        resolve();
      }
    }, BRDF_EXPANSION_POLL_MS);
  });
}

function initState(params: Params): BabylonState {
  params.callbacks.onLoadingStatusChanged(true);

  const engine = new babylon.Engine(params.canvas);
  engine.renderEvenInBackground = false;
  const scene = initScene(engine, params.theme, params.callbacks);

  const state: BabylonState = {
    entities: {},
    sceneConfig: { entityGeneration: 0 },
    input: {
      cameraDeltas: { ...ZERO_CAMERA_DELTAS },
      tool: { mode: "none" },
    },
    context: {
      systemSchedules: {},
      dataServer: params.dataServer,
      modelLoader: params.modelLoader,
      callbacks: params.callbacks,
      canvas: params.canvas,
      engine,
      scene,
    },
  };
  systems.onSetState(state, params.initialState);
  systems.onSetTime(state, params.time);
  scheduleSystems(state);
  return state;
}

const RELOAD_STALE_DATA_INTERVAL_MS = 50;

function scheduleSystems(state: BabylonState) {
  const systemSchedules = state.context.systemSchedules;

  const engine = state.context.engine;
  const render = () => {
    systems.render(state);
  };
  engine.runRenderLoop(render);
  systemSchedules["RENDER"] = {
    dispose: () => engine.stopRenderLoop(render),
  };

  const resize = () => {
    systems.resize(state);
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(state.context.canvas);
  systemSchedules["RESIZE"] = {
    dispose: () => resizeObserver.disconnect(),
  };

  const reloadStaleDataInterval = window.setInterval(() => {
    systems.reloadStaleComponents(state);
  }, RELOAD_STALE_DATA_INTERVAL_MS);
  systemSchedules["RELOAD_STALE_DATA"] = {
    dispose: () => window.clearInterval(reloadStaleDataInterval),
  };

  // Third-person input handlers attach for the BabylonManager's
  // lifetime; each handler gates internally on the active camera type
  // and no-ops when an orbit camera is active. Babylon's default
  // `CameraInputsManager` pipeline is uninstalled by
  // `babylonCamera.inputs.clear()` inside `buildThirdPersonCamera`
  // (see `updateSceneConfig`), so these three handlers are the only
  // input path for the third-person camera.
  //
  // Each handler is registered as its own schedule so a synchronous
  // throw during one attach does not leave earlier handlers wired but
  // unrecorded.
  systemSchedules["THIRD_PERSON_ORBIT_INPUT"] = {
    dispose: systems.attachOrbitInput(state),
  };
  systemSchedules["THIRD_PERSON_ZOOM_INPUT"] = {
    dispose: systems.attachZoomInput(state),
  };
  systemSchedules["THIRD_PERSON_PAN_INPUT"] = {
    dispose: systems.attachPanInput(state),
  };

  // Unlike the three above, the measure tool works in either camera mode,
  // so this handler gates its clicks on whether the tool is on rather
  // than on the camera type. It writes into `state.input.tool` (the
  // cursor position, mirrored mode-blind, and resolved measure clicks),
  // and the render loop consumes those records once per frame.
  systemSchedules["TOOL_POINTER_INPUT"] = {
    dispose: systems.attachToolPointerInput(state),
  };
}
